<template>
  <div :class="ns.b()">
    <header :class="ns.e('header')">
      <div>
        <h1>知识库 / 向量状态</h1>
        <p>查看 Milvus 向量集合、Embedding 配置与已入库文档。</p>
      </div>
      <z-button type="primary" :loading="loading" @click="refreshAll">刷新</z-button>
    </header>

    <p v-if="error" :class="ns.e('error')">{{ error }}</p>

    <section :class="ns.e('cards')">
      <article :class="ns.e('stat')">
        <span :class="ns.e('stat-label')">Milvus</span>
        <strong :class="[ns.e('stat-value'), ns.is('ok', milvusOk), ns.is('bad', !milvusOk)]">
          {{ milvusOk ? '已连接' : '不可用' }}
        </strong>
        <span :class="ns.e('stat-meta')">{{ status?.milvus_uri || '—' }}</span>
      </article>
      <article :class="ns.e('stat')">
        <span :class="ns.e('stat-label')">Collection</span>
        <strong :class="ns.e('stat-value')">{{ status?.milvus_collection || '—' }}</strong>
        <span :class="ns.e('stat-meta')">
          {{ status?.collection_exists ? '已创建' : '未创建' }}
          · 实体 {{ formatCount(status?.entity_count) }}
          · 维度 {{ status?.vector_dim ?? '—' }}
        </span>
      </article>
      <article :class="ns.e('stat')">
        <span :class="ns.e('stat-label')">Embedding</span>
        <strong
          :class="[
            ns.e('stat-value'),
            ns.is('ok', status?.embedding_configured),
            ns.is('warn', !status?.embedding_configured),
          ]"
        >
          {{ status?.embedding_configured ? '已配置' : '未配置' }}
        </strong>
        <span :class="ns.e('stat-meta')">{{ status?.embedding_model || '—' }}</span>
      </article>
      <article :class="ns.e('stat')">
        <span :class="ns.e('stat-label')">文档 / Chunk</span>
        <strong :class="ns.e('stat-value')">
          {{ status?.documents_total ?? 0 }} / {{ status?.chunks_indexed ?? 0 }}
        </strong>
        <span :class="ns.e('stat-meta')">{{ statusSummary }}</span>
      </article>
    </section>

    <p v-if="status?.error" :class="ns.e('warn')">Milvus 详情：{{ status.error }}</p>
    <p v-if="status?.embedding_base_url" :class="ns.e('hint')">
      Embedding Base：{{ status.embedding_base_url }}
    </p>

    <section :class="ns.e('toolbar')">
      <z-select
        v-model="filterSpaceId"
        clearable
        placeholder="全部空间"
        style="width: 200px"
        @change="onFilterChange"
      >
        <z-option
          v-for="space in spaces"
          :key="space.id"
          :label="space.name"
          :value="space.id"
        />
      </z-select>
      <z-select
        v-model="filterStatus"
        clearable
        placeholder="全部状态"
        style="width: 140px"
        @change="onFilterChange"
      >
        <z-option v-for="s in statusOptions" :key="s" :label="statusLabel(s)" :value="s" />
      </z-select>
      <span :class="ns.e('total')">共 {{ total }} 篇</span>
    </section>

    <z-table
      v-loading="docsLoading"
      :data="documents"
      stripe
      border
      height="100%"
      :class="ns.e('table')"
      empty-text="暂无文档，请先通过 /v1/documents/index 或 upload 入库"
      @row-click="openChunks"
    >
      <z-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
      <z-table-column prop="space_name" label="空间" width="120" show-overflow-tooltip>
        <template #default="{ row }">{{ row.space_name || row.space_id.slice(0, 8) }}</template>
      </z-table-column>
      <z-table-column prop="status" label="状态" width="110">
        <template #default="{ row }">
          <span :class="[ns.e('pill'), `is-${row.status}`]">{{ statusLabel(row.status) }}</span>
        </template>
      </z-table-column>
      <z-table-column prop="chunk_count" label="Chunks" width="90" align="right" />
      <z-table-column prop="source" label="来源" width="110" />
      <z-table-column prop="updated_at" label="更新时间" width="170">
        <template #default="{ row }">{{ formatTime(row.updated_at) }}</template>
      </z-table-column>
      <z-table-column label="操作" width="100" fixed="right">
        <template #default="{ row }">
          <z-button link type="primary" @click.stop="openChunks(row)">查看分片</z-button>
        </template>
      </z-table-column>
    </z-table>

    <div :class="ns.e('pager')">
      <z-pagination
        v-model:current-page="page"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[20, 50, 100]"
        layout="total, sizes, prev, pager, next"
        @current-change="loadDocuments"
        @size-change="onPageSizeChange"
      />
    </div>

    <z-drawer
      v-model="drawerOpen"
      :title="selectedDoc ? `分片 · ${selectedDoc.title}` : '分片'"
      size="480px"
      destroy-on-close
    >
      <p v-if="chunksError" :class="ns.e('error')">{{ chunksError }}</p>
      <p v-else-if="chunksLoading" :class="ns.e('hint')">加载中…</p>
      <p v-else-if="!chunks.length" :class="ns.e('hint')">
        未从 Milvus 读到分片（文档可能未就绪，或 collection 为空）。
      </p>
      <div v-else :class="ns.e('chunk-list')">
        <article v-for="chunk in chunks" :key="chunk.chunk_id" :class="ns.e('chunk')">
          <header>
            <span>#{{ chunk.chunk_index }}</span>
            <code>{{ chunk.chunk_id }}</code>
          </header>
          <pre>{{ chunk.text }}</pre>
        </article>
      </div>
      <template v-if="selectedDoc?.error_message" #footer>
        <p :class="ns.e('error')">索引错误：{{ selectedDoc.error_message }}</p>
      </template>
    </z-drawer>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import type {
  SeekerDocument,
  SeekerDocumentChunk,
  SeekerSpace,
  SeekerVectorStatus
} from './types'
import { useAiClient } from './setup'

defineOptions({ name: 'ZAiKnowledge' })

const ns = useNamespace('ai-knowledge')
const client = useAiClient()

const statusOptions = ['pending', 'parsing', 'embedding', 'ready', 'failed'] as const

const loading = ref(false)
const docsLoading = ref(false)
const error = ref('')
const status = ref<SeekerVectorStatus | null>(null)
const spaces = ref<SeekerSpace[]>([])
const documents = ref<SeekerDocument[]>([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(50)
const filterSpaceId = ref<string | undefined>()
const filterStatus = ref<string | undefined>()

const drawerOpen = ref(false)
const selectedDoc = ref<SeekerDocument | null>(null)
const chunks = ref<SeekerDocumentChunk[]>([])
const chunksLoading = ref(false)
const chunksError = ref('')

const milvusOk = computed(
  () => Boolean(status.value?.milvus_reachable && !status.value?.error)
)

const statusSummary = computed(() => {
  const map = status.value?.documents_by_status || {}
  const parts = Object.entries(map)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${statusLabel(k)} ${n}`)
  return parts.length ? parts.join(' · ') : '无入库文档'
})

function statusLabel(value: string): string {
  const map: Record<string, string> = {
    pending: '待处理',
    parsing: '解析中',
    embedding: '向量化中',
    ready: '就绪',
    failed: '失败'
  }
  return map[value] || value
}

function formatCount(value?: number | null): string {
  if (value == null) return '—'
  return String(value)
}

function formatTime(value?: string): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

async function loadStatus(): Promise<void> {
  status.value = await client.vectorStatus()
}

async function loadSpaces(): Promise<void> {
  spaces.value = await client.listSpaces()
}

async function loadDocuments(): Promise<void> {
  docsLoading.value = true
  try {
    const offset = (page.value - 1) * pageSize.value
    const resp = await client.listDocuments({
      spaceId: filterSpaceId.value || null,
      status: filterStatus.value || null,
      limit: pageSize.value,
      offset
    })
    documents.value = resp.items
    total.value = resp.total
  } finally {
    docsLoading.value = false
  }
}

async function refreshAll(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    await Promise.all([loadStatus(), loadSpaces(), loadDocuments()])
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

function onFilterChange(): void {
  page.value = 1
  void loadDocuments().catch((err) => {
    error.value = err instanceof Error ? err.message : String(err)
  })
}

function onPageSizeChange(): void {
  page.value = 1
  void loadDocuments()
}

async function openChunks(row: SeekerDocument): Promise<void> {
  selectedDoc.value = row
  drawerOpen.value = true
  chunks.value = []
  chunksError.value = ''
  chunksLoading.value = true
  try {
    const resp = await client.listDocumentChunks(row.id, { limit: 100 })
    chunks.value = resp.items
  } catch (err) {
    chunksError.value = err instanceof Error ? err.message : String(err)
  } finally {
    chunksLoading.value = false
  }
}

onMounted(() => {
  void refreshAll()
})
</script>
